import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { isEqual } from 'lodash';

import { BasicAssumption } from '@futrli-sdk/consolidations';
import { useCrudActivityContext } from '@futrli/shared-services';

import {
  checkIfFreestyleContentIsEmpty,
  getFormattedAmounts,
  getFreestyleInitialContent,
  getUpdateFreestyleInitialContent
} from './utils';
import { AllowedAssumptionTypes } from '../../../../detail/reports/actions/actions';
import { FreestyleMethodState, PaymentTreatment, PredictionType } from '../../../types';
import { FreestyleContent } from '../../../components/freestyle/freestyle-content';
import { PredictionEditor } from '../prediction-editor/prediction-editor';
import { PredictionHeader } from '../prediction-header/prediction-header';
import { ASSUMPTION_TYPES, TRACKING_EVENTS } from '../../../../../core/constants';
import { trackEvent } from '../../../../../core/tracker';
import { UserGridSettingsContext } from '../../../../detail/reports/user-grid-settings/user-grid-settings-context';
import { useConsolidatedOrganisationPredictionBuilderContext } from '../../../context/consolidated-organisation-prediction-builder-context';
import { generateFinancialYearsData } from './utils';

interface Props {
  onClose?: () => void;
  isPredictionTypeActive?: boolean;
}

export const FreestylePrediction = ({ onClose, isPredictionTypeActive }: Props) => {
  const {
    accountIndex,
    currency,
    financialConfig,
    forecast,
    handleSaveExisting,
    handleSaveNew,
    isCashFlowDisabled,
    isEditing,
    isQuickCopy,
    paymentTreatment,
    predictionData,
    predictionName,
    predictionType,
    reloadForecastData,
    reset,
    resetUrlData,
    setImpactPreviewParams,
    setIsSaveDisabled,
    setPredictionContentTouched,
    urlPredictionId,
    vatPercentage,
    previewImpactsResponse
  } = useConsolidatedOrganisationPredictionBuilderContext();

  const { addCreating, removeCreating } = useCrudActivityContext();
  const { showDecimals } = useContext(UserGridSettingsContext);

  const [freestyleContent, setFreestyleContent] = useState<FreestyleMethodState>();
  const [storeOnThe, setStoreOnThe] = useState('1');

  const forecastFromDate = forecast?.forecast_from_date;
  const isActive = predictionType === PredictionType.FREESTYLE;
  const isFirstRun = useRef(true);
  const lastImpactPreviewParams = useRef<Partial<AllowedAssumptionTypes>>();

  // *********
  // * Utils *
  // *********

  const getRequestData = useCallback(() => {
    const params: Partial<BasicAssumption> = {
      name: predictionName,
      vat_percentage: paymentTreatment === PaymentTreatment.NON_CASH_TRANSFER ? '0' : vatPercentage || null,
      account_id: accountIndex,
      excluded_from_report: false,
      invoice_date: storeOnThe as BasicAssumption['invoice_date'],
      amounts: freestyleContent ? getFormattedAmounts(freestyleContent) : undefined
    };
    return params;
  }, [accountIndex, freestyleContent, paymentTreatment, predictionName, storeOnThe, vatPercentage]);

  const refreshAccountsImpactPreview = useCallback(async () => {
    if (accountIndex && !isCashFlowDisabled) {
      const newParams = {
        ...getRequestData(),
        name: 'freestyle-impact-preview'
      };
      if (!isEqual(newParams, lastImpactPreviewParams.current)) {
        lastImpactPreviewParams.current = newParams;
        setImpactPreviewParams(newParams);
      }
    }
  }, [accountIndex, getRequestData, isCashFlowDisabled, setImpactPreviewParams]);

  const restoreInitialContent = useCallback(() => {
    if (financialConfig) {
      setFreestyleContent(getFreestyleInitialContent(financialConfig, forecastFromDate, forecast?.kind));
      setStoreOnThe('1');
      return true;
    }
    return false;
  }, [financialConfig, forecastFromDate, forecast?.kind]);

  const restoreLoadedContent = useCallback(() => {
    if (financialConfig && predictionData && predictionData.assumption_type === ASSUMPTION_TYPES.BASIC) {
      setStoreOnThe((predictionData.assumption_data as BasicAssumption).invoice_date || '1');
      setFreestyleContent(
        getUpdateFreestyleInitialContent(
          financialConfig,
          (predictionData.assumption_data as BasicAssumption).amounts,
          showDecimals,
          forecastFromDate,
          forecast?.kind
        )
      );
      return true;
    }
    return false;
  }, [financialConfig, forecastFromDate, forecast?.kind, predictionData, showDecimals]);

  const save = useCallback(async () => {
    const params = getRequestData();

    if (urlPredictionId && !isQuickCopy) {
      // Update existing
      return await handleSaveExisting(params);
    } else {
      // Save new
      const newAssumption = await handleSaveNew(params);
      if (newAssumption) {
        trackEvent(TRACKING_EVENTS.CREATED_ANY_MANUAL_PREDICTION);
        trackEvent(TRACKING_EVENTS.ADD_PREDICTION_FORMULA_CONSOLIDATION);
        return true;
      }
    }

    return false;
  }, [getRequestData, handleSaveExisting, handleSaveNew, isQuickCopy, urlPredictionId]);

  // ************
  // * Handlers *
  // ************

  const handleFreestyleContentChange = useCallback(
    (content: FreestyleMethodState) => {
      if (!checkIfFreestyleContentIsEmpty(content)) {
        setPredictionContentTouched(true);
      }
      setFreestyleContent(content);
    },
    [setPredictionContentTouched]
  );

  const handleAddFinancialYear = useCallback(() => {
    if (freestyleContent && financialConfig) {
      const lastFinancialYear = Object.keys(freestyleContent)
        .map(k => Number(k))
        .sort((a, b) => b - a)[0];

      const newContent = {
        ...freestyleContent,
        ...generateFinancialYearsData(financialConfig, lastFinancialYear + 1, lastFinancialYear + 1)
      };

      handleFreestyleContentChange(newContent);
    }
  }, [freestyleContent, financialConfig, handleFreestyleContentChange]);

  const handleSave = useCallback(async () => {
    addCreating('freestyle-prediction');
    const success = await save();
    if (success) {
      reloadForecastData();
      onClose?.();
    }
    removeCreating('freestyle-prediction');
  }, [addCreating, onClose, reloadForecastData, removeCreating, save]);

  const handleSaveAndNew = useCallback(async () => {
    addCreating('freestyle-prediction');
    const success = await save();
    if (success) {
      reloadForecastData();
      reset();
      resetUrlData();
      restoreInitialContent();
    }
    removeCreating('freestyle-prediction');
  }, [addCreating, reloadForecastData, removeCreating, reset, restoreInitialContent, save, resetUrlData]);

  const handleStoreOnTheChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStoreOnThe(e.target.value);
    setPredictionContentTouched(true);
  };

  // ***********
  // * Effects *
  // ***********

  // Set initial / restore saved content on first run
  useEffect(() => {
    if (isFirstRun.current) {
      let contentWasRestored = false;

      if (isEditing || isQuickCopy) {
        if (predictionData) {
          if (predictionData.assumption_type === ASSUMPTION_TYPES.BASIC) {
            contentWasRestored = restoreLoadedContent();
          } else {
            contentWasRestored = restoreInitialContent();
          }
        }
      } else {
        contentWasRestored = restoreInitialContent();
      }

      isFirstRun.current = !contentWasRestored;
    }
  }, [isEditing, predictionData, restoreInitialContent, restoreLoadedContent, isQuickCopy]);

  // Refresh the accounts impacted info
  useEffect(() => {
    if (isActive) {
      refreshAccountsImpactPreview();
    } else {
      lastImpactPreviewParams.current = undefined;
    }
  }, [isActive, refreshAccountsImpactPreview]);

  // Update isSaveDisabled flag
  useEffect(() => {
    if (isActive) {
      setIsSaveDisabled(false);
    }
  }, [isActive, setIsSaveDisabled]);

  return (
    <PredictionEditor
      onSave={handleSave}
      onSaveAndNew={handleSaveAndNew}
      show={isActive}
      onClose={onClose}
      isPredictionTypeActive={isPredictionTypeActive}
    >
      <FreestyleContent
        currency={currency}
        PredictionHeader={PredictionHeader}
        freestyleContent={freestyleContent}
        onAddFinancialYear={handleAddFinancialYear}
        onChange={handleFreestyleContentChange}
        storeOnThe={storeOnThe}
        handleStoreOnTheChange={handleStoreOnTheChange}
        previewImpacts={previewImpactsResponse}
      />
    </PredictionEditor>
  );
};
